import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import PageHeader, { ToolbarCaptionButton } from '@/components/PageHeader';
import { listPublicStories } from '@/services/storyRepo';
import SearchFilters from '@/components/SearchFilters';
import StoryListCard from '@/components/StoryListCard';
import Seo from '@/components/Seo';
import { useTagOptions } from '@/hooks/useTagOptions';
import {
  FILTER_DIMENSIONS,
  paramsToSet,
  setToParam,
  selectedFromParams,
  selectedToFiltersPayload,
  totalSelectedCount,
} from '@/lib/filters';

const PAGE_SIZE = 12;

// Browse uses the same URL-as-source-of-truth pattern as SearchResultsPage so
// filter state survives reload + back/forward, and the shared <SearchFilters />
// component renders every dimension declared in FILTER_DIMENSIONS (currently
// 7: profession, life area, energy arc, what shifted, inner knot, obstacle,
// mental state, practice). Adding a new dimension to filters.js surfaces it
// here automatically.
const BrowseStoriesPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);

  const { tagOptions } = useTagOptions();
  const selected = useMemo(() => selectedFromParams(searchParams), [searchParams]);
  const totalSelected = totalSelectedCount(selected);
  const filtersPayload = useMemo(() => selectedToFiltersPayload(selected), [selected]);

  // Pass `pageOverride` explicitly so loadMore doesn't race React state.
  const loadStories = useCallback(async (reset, pageOverride = 0) => {
    setLoading(true);
    setLoadError(null);
    const currentPage = reset ? 0 : pageOverride;
    try {
      const { stories: fetched, total: fetchedTotal, has_more } = await listPublicStories({
        limit: PAGE_SIZE,
        offset: currentPage * PAGE_SIZE,
        ...filtersPayload,
      });
      setStories((prev) => (reset ? fetched : [...prev, ...fetched]));
      setTotal(fetchedTotal);
      setHasMore(has_more);
      if (reset) setPage(0);
    } catch (err) {
      console.error('Error loading stories:', err);
      setLoadError(err?.message || 'Failed to load stories');
    } finally {
      setLoading(false);
    }
  }, [filtersPayload]);

  // Whenever filters change (URL change), reset list + reload from page 0.
  useEffect(() => {
    loadStories(true);
  }, [loadStories]);

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadStories(false, nextPage);
  };

  // Filter callbacks — write straight to the URL, mirroring SearchResultsPage.
  const handleToggleTag = (dimensionKey, tagId) => {
    const param = FILTER_DIMENSIONS.find((d) => d.key === dimensionKey)?.param;
    if (!param) return;
    const next = new URLSearchParams(searchParams);
    const current = paramsToSet(next.get(param));
    if (current.has(tagId)) current.delete(tagId);
    else current.add(tagId);
    if (current.size) next.set(param, setToParam(current));
    else next.delete(param);
    setSearchParams(next);
  };

  const handleClearFilters = () => {
    const next = new URLSearchParams(searchParams);
    FILTER_DIMENSIONS.forEach(({ param }) => next.delete(param));
    setSearchParams(next);
  };

  const matchSummary = useMemo(() => {
    if (loading && stories.length === 0) return 'Loading…';
    if (totalSelected === 0) return undefined;
    if (total === 0) return 'No matches';
    return `${total} ${total === 1 ? 'story' : 'stories'}`;
  }, [loading, stories.length, totalSelected, total]);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Seo
        title="Browse all stories · Katha Mithra"
        description="Read every published story on Katha Mithra — real accounts of inner transformation rooted in spiritual practice, newest first."
        path="/browse"
      />
      <PageHeader>
        <ToolbarCaptionButton
          label="Back to Home"
          shortLabel="Home"
          onClick={() => navigate('/')}
          data-testid="back-home-btn"
        />
      </PageHeader>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif tracking-tight leading-tight text-[#2D2A26] mb-4">
            Browse All Stories
          </h2>
          <p className="text-base md:text-lg leading-relaxed text-[#6B665E]">
            {total} transformation {total === 1 ? 'story' : 'stories'} to inspire you
          </p>
        </div>

        <div className="mb-8">
          <SearchFilters
            selected={selected}
            tagOptions={tagOptions}
            onToggleTag={handleToggleTag}
            onClearAll={handleClearFilters}
            matchSummary={matchSummary}
          />
        </div>

        {loading && stories.length === 0 ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#C05621]" />
            <p className="mt-4 text-[#6B665E]">Loading stories...</p>
          </div>
        ) : loadError ? (
          <div className="text-center py-12 bg-white rounded-xl border border-red-200">
            <p className="text-xl text-[#2D2A26] font-serif">Couldn't load stories</p>
            <p className="text-sm text-[#6B665E] mt-2 max-w-md mx-auto break-words">{loadError}</p>
            <Button
              onClick={() => loadStories(true)}
              className="mt-6 bg-[#C05621] hover:bg-[#A0461A] text-white"
              data-testid="retry-load-btn"
            >
              Retry
            </Button>
          </div>
        ) : stories.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="stories-grid">
              {stories.map((story) => (
                <StoryListCard
                  key={story.id}
                  story={story}
                  tagOptions={tagOptions}
                  onClick={() => navigate(`/story/${story.id}`)}
                />
              ))}
            </div>

            {hasMore && (
              <div className="text-center mt-12">
                <Button
                  onClick={loadMore}
                  disabled={loading}
                  className="rounded-full px-8 py-6 text-lg font-medium bg-[#C05621] hover:bg-[#A0461A] text-white shadow-lg hover:shadow-xl"
                  data-testid="load-more-btn"
                >
                  {loading ? 'Loading...' : 'Load More Stories'}
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12 bg-white rounded-xl border border-[#E6E2D6]">
            <p className="text-xl text-[#2D2A26] font-serif">No stories found</p>
            <p className="text-sm text-[#6B665E] mt-2">
              {totalSelected > 0
                ? 'No stories match these filters yet — try clearing some.'
                : 'Be the first to share a story!'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BrowseStoriesPage;
